import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as action from '../../../Action/BulkSeriesImport/BulkSeriesImport_Action';
import * as seriesAction from '../../../Action/Series/Series_Action';
import { errorMessage, newRequestId } from './helpers';
import { episodeValue, missingEpisodeRanges, renamedFile, renumberInOrder } from './importNames';

const FILE_TAG = {
    trailer: ['trailer', 'blue'],
    episode: [null, 'green'], // label is "Episode N"
    skipped: ['Skipped', 'red'],
    ignored: ['Ignored', 'gray'],
};

const Steps = ({ current }) => {
    const steps = ['Type', 'Folders', 'Review'];
    return (
        <ol aria-label="Steps" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', alignItems: 'center', gap: 16 }}>
            {steps.map((label, i) => {
                const n = i + 1;
                const done = n < current;
                const on = n === current;
                return (
                    <React.Fragment key={label}>
                        {i > 0 && <li aria-hidden="true" style={{ width: 48, height: 1, background: 'rgba(255,255,255,.14)' }} />}
                        <li className="bsi-step" aria-current={on ? 'step' : undefined} style={{ color: on ? '#fff' : done ? '#4ade80' : undefined }}>
                            <span className="bsi-step-dot" style={on ? { background: '#00A8F3', borderColor: '#00A8F3', color: '#0a0a12' } : done ? { borderColor: 'rgba(53,173,101,.6)' } : undefined}>
                                {done ? <i className="fas fa-check" aria-hidden="true" /> : n}
                            </span>
                            {label}
                        </li>
                    </React.Fragment>
                );
            })}
        </ol>
    );
};

// ── Step 1: what are you importing ─────────────────────────────────────────

const SERIES_PAGE_SIZE = 20;
// Fetch the next page once the 15th row of the newest page scrolls into view,
// so more series are usually loaded before the admin reaches the end.
const PREFETCH_ROW = 15;

/** Scrollable series list: first 20 on open, more on scroll, search restarts it. */
const SeriesPicker = ({ targetSeries, setTargetSeries }) => {
    const dispatch = useDispatch();
    const [query, setQuery] = useState('');
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    // A failed page stops auto-loading until the admin clicks Retry — otherwise
    // the still-visible trigger row refires at once and hammers the API (and
    // its 120 req/min rate limit) in a loop.
    const [failed, setFailed] = useState(false);
    const [exhausted, setExhausted] = useState(false); // a page added nothing new
    const listRef = useRef(null);
    const seq = useRef(0); // drops responses for an older search/page
    const requestedPage = useRef(0); // highest page already asked for — never twice
    const failedRef = useRef(false); // read by observer callbacks created before a failure
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const load = useCallback((nextPage, search) => {
        const mine = ++seq.current;
        requestedPage.current = nextPage;
        failedRef.current = false;
        setLoading(true);
        setFailed(false);
        dispatch(seriesAction.getSeries({ search, page: nextPage, per_page: SERIES_PAGE_SIZE }))
            .then((res) => {
                if (mine !== seq.current) return;
                const rd = res?.responseDetails || [];
                const rows = Array.from(Array.isArray(rd) ? rd : (rd.data || rd.rows || []));
                const prev = nextPage === 1 ? [] : itemsRef.current;
                const fresh = rows.filter((r) => !prev.some((p) => p.id === r.id));
                setItems([...prev, ...fresh]);
                setTotal(Number(rd.totalRecords ?? rd.total ?? rows.length) || 0);
                setExhausted(fresh.length === 0 || rows.length < SERIES_PAGE_SIZE);
                setPage(nextPage);
            })
            .catch((err) => {
                if (mine !== seq.current) return;
                requestedPage.current = nextPage - 1; // so Retry can ask for it again
                failedRef.current = true;
                setFailed(true);
                toast.error(errorMessage(err, 'Failed to load series'), { toastId: 'bsi-series-load' });
            })
            .finally(() => { if (mine === seq.current) setLoading(false); });
    }, [dispatch]);

    // First page right away; a new search (after the admin stops typing) starts over.
    useEffect(() => {
        const t = setTimeout(() => load(1, query.trim()), query ? 300 : 0);
        return () => clearTimeout(t);
    }, [query, load]);

    const hasMore = !exhausted && items.length < total;
    // Scrolling never loads after a failure; only the Retry button (manual) does.
    const loadNext = ({ manual = false } = {}) => {
        if (loading || requestedPage.current > page || (failedRef.current && !manual)) return;
        load(page + 1, query.trim());
    };
    const triggerIndex = Math.max(0, items.length - SERIES_PAGE_SIZE + PREFETCH_ROW - 1); // 0-based
    const observer = useRef(null);
    const triggerRef = useCallback((node) => {
        if (observer.current) observer.current.disconnect();
        if (!node || !hasMore || loading || failed || typeof IntersectionObserver === 'undefined') return;
        observer.current = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) loadNext();
        }, { root: listRef.current });
        observer.current.observe(node);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadNext reads the same deps listed here
    }, [hasMore, loading, failed, page, query]);
    useEffect(() => () => observer.current?.disconnect(), []);

    return (
        <div className="bsi-card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label htmlFor="bsi-series-search" style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Choose the series</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <input id="bsi-series-search" type="search" className="bsi-input" style={{ width: 420, maxWidth: '100%' }}
                        placeholder="Filter by name…" value={query} onChange={(e) => setQuery(e.target.value)} />
                    {targetSeries && (
                        <span style={{ fontSize: 13 }}>Selected: <strong style={{ color: '#fff' }}>{targetSeries.title}</strong> <span className="bsi-muted">{targetSeries.id}</span></span>
                    )}
                </div>
            </div>
            <div ref={listRef} role="radiogroup" aria-label="Series" style={{ maxHeight: 380, overflowY: 'auto', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                {items.map((s, i) => {
                    const active = Number(s.is_active) === 1;
                    const chosen = targetSeries?.id === s.id;
                    return (
                        <label key={s.id} ref={i === triggerIndex ? triggerRef : undefined} className="bsi-row"
                            style={{ gridTemplateColumns: '28px minmax(0,1fr) 110px 160px', cursor: active ? 'not-allowed' : 'pointer', opacity: active ? 0.7 : 1, background: chosen ? 'rgba(0,168,243,.07)' : undefined, borderTop: i === 0 ? 'none' : undefined }}>
                            <input type="radio" name="bsi-target" className="bsi-cb" disabled={active} checked={chosen} onChange={() => setTargetSeries(s)} />
                            <span className="bsi-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
                            <span className="bsi-muted" style={{ fontSize: 13 }}>{s.id}</span>
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {active
                                    ? <><span className="bsi-badge green" style={{ alignSelf: 'flex-start' }}>Active</span><span style={{ fontSize: 12, color: '#facc15' }}>Mark INACTIVE first</span></>
                                    : <span className="bsi-badge gray" style={{ alignSelf: 'flex-start' }}>Inactive</span>}
                            </span>
                        </label>
                    );
                })}
                {loading && <div className="bsi-row bsi-muted" style={{ borderTop: items.length ? undefined : 'none' }}>Loading series…</div>}
                {!loading && failed && (
                    <div className="bsi-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', borderTop: items.length ? undefined : 'none' }}>
                        <span style={{ color: '#ff9e94', fontSize: 13 }}>Couldn't load {items.length ? 'more ' : ''}series.</span>
                        <button type="button" className="bsi-btn ghost sm" onClick={() => (items.length ? loadNext({ manual: true }) : load(1, query.trim()))}>Retry</button>
                    </div>
                )}
                {!loading && !failed && items.length === 0 && <div className="bsi-row bsi-muted" style={{ borderTop: 'none' }}>{query.trim() ? `No series match "${query.trim()}".` : 'No series yet.'}</div>}
                {!loading && !failed && !hasMore && items.length > 0 && (
                    <div className="bsi-row bsi-muted" style={{ fontSize: 13, justifyContent: 'center' }}>All {total} series shown</div>
                )}
            </div>
        </div>
    );
};

const TypeStep = ({ mode, setMode, targetSeries, setTargetSeries, onCancel, onNext }) => {
    const option = (value, title, body, note) => (
        <label className={`bsi-option ${mode === value ? 'on' : ''}`}>
            <input type="radio" name="bsi-mode" className="bsi-cb" style={{ marginTop: 2 }} checked={mode === value} onChange={() => { setMode(value); setTargetSeries(null); }} />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{title}</span>
                <span style={{ fontSize: 14, lineHeight: 1.5, color: '#9a9aaa' }}>{body}</span>
                <span className="bsi-muted" style={{ fontSize: 13 }}>{note}</span>
            </span>
        </label>
    );

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18 }}>
                {option('NEW_SERIES', 'New series with episodes',
                    'Each folder you pick becomes a new series. Its MP4 files become episodes and the trailer is detected automatically.',
                    'Created in DRAFT mode | Category: Moderation (ID: 36) | A placeholder poster | U/A 16+')}
                {option('ADD_EPISODES', 'Episodes for an existing series',
                    "Add a folder's episodes to a series that already exists. Episode numbers that already exist are replaced.",
                    'The series must be in DRAFT mode')}
            </div>

            {mode === 'ADD_EPISODES' && (
                <>
                    <SeriesPicker targetSeries={targetSeries} setTargetSeries={setTargetSeries} />
                    <div className="bsi-callout amber">
                        <i className="fas fa-info-circle" style={{ color: '#facc15', marginTop: 3 }} aria-hidden="true" />
                        <span>Why DRAFT? Viewers of a live series would hit playback errors while the new episodes transcode. Deactivate it, import, check the episodes, then publish again.</span>
                    </div>
                </>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button type="button" className="bsi-btn ghost" onClick={onCancel}>Cancel</button>
                <button type="button" className="bsi-btn primary" disabled={mode === 'ADD_EPISODES' && !targetSeries} onClick={onNext}>
                    Next: pick folders <i className="fas fa-chevron-right" aria-hidden="true" />
                </button>
            </div>
        </>
    );
};

// ── Step 2: pick folders in S3 ─────────────────────────────────────────────

// Which files the admin can tick: episodes and the trailer (ticked by default),
// plus files skipped only because they clash — two files with one episode
// number, or several trailers — so ticking one of them resolves the clash.
// Unnumbered and non-MP4 files can't be imported, so they're not tickable.
const isTrailerLike = (f) => f.kind === 'trailer' || (!f.invalidName && f.kind === 'skipped' && /trailer/i.test(f.reason || ''));
const isClash = (f) => !f.invalidName && f.kind === 'skipped' && (f.episodeNumber != null || /trailer/i.test(f.reason || ''));
const isPickable = (f) => f.isMp4 && (f.kind === 'episode' || f.kind === 'trailer' || isClash(f));
const isDefaultPick = (f) => f.kind === 'episode' || f.kind === 'trailer';

/** What the current ticks would import, and any clash still ticked. */
function summarisePicks(files, picked) {
    const chosen = files.filter((f) => picked.has(f.name));
    const episodes = chosen.filter((f) => !isTrailerLike(f));
    const trailers = chosen.filter(isTrailerLike);
    const perNumber = new Map();
    episodes.forEach((f) => perNumber.set(f.episodeNumber, (perNumber.get(f.episodeNumber) || 0) + 1));
    const clashes = [...perNumber].filter(([, n]) => n > 1).map(([num]) => num).sort((a, b) => a - b);
    const missing = missingEpisodeRanges(episodes.map((file) => file.episodeNumber));
    return { episodes: episodes.length, trailer: trailers.length === 1, trailerClash: trailers.length > 1, clashes, missing };
}

function rootFolderTitle(path) {
    const root = path.split('/').filter(Boolean)[0] || '';
    try { return decodeURIComponent(root); } catch { return root; }
}

const FolderStep = ({ mode, targetSeries, onBack, onCreated }) => {
    const dispatch = useDispatch();
    const [rootUri, setRootUri] = useState('');
    const [prefix, setPrefix] = useState('');
    const [folders, setFolders] = useState([]);
    const [sourceFiles, setFiles] = useState([]);
    const [renames, setRenames] = useState({});
    const [includeSubfolders, setIncludeSubfolders] = useState(false);
    const files = sourceFiles.map((file) => renames[file.name] === undefined ? file : renamedFile(file, renames[file.name]));
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [browseFailed, setBrowseFailed] = useState(false);
    const browseRequest = useRef(0);
    const rootRef = useRef('');
    // Save source picks, name overrides and browse mode alongside the API selection.
    const [selected, setSelected] = useState([]);
    const [picked, setPicked] = useState(new Set()); // ticked file names in the open folder
    const [label, setLabel] = useState('');
    const [creating, setCreating] = useState(false);
    const [existingMatches, setExistingMatches] = useState([]);
    const [existingChoices, setExistingChoices] = useState({});
    // One id per wizard: a double-clicked create returns the same batch.
    const requestId = useRef(newRequestId());
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    const browse = useCallback((nextPrefix, recursive = selectedRef.current.find((s) => s.path === nextPrefix)?.includeSubfolders || false, draft = null) => {
        const request = ++browseRequest.current;
        setLoading(true);
        setBrowseFailed(false);
        setIncludeSubfolders(recursive);
        dispatch(action.getBulkImportBrowse({ prefix: nextPrefix, ...(recursive && { include_subfolders: true }) }))
            .then((res) => {
                if (request !== browseRequest.current) return;
                const data = res?.responseDetails || {};
                const currentRoot = data.rootUri || '';
                // Selections from an old configured root must not carry over.
                if (rootRef.current && currentRoot !== rootRef.current) {
                    selectedRef.current = [];
                    setSelected([]);
                }
                rootRef.current = currentRoot;
                setRootUri(currentRoot);
                const openedPrefix = data.prefix ?? nextPrefix ?? '';
                const openedFiles = data.files || [];
                setPrefix(openedPrefix);
                setFolders(data.folders || []);
                setFiles(openedFiles);
                setPreview(data.seriesPreview || null);
                // Re-opening a selected folder shows its saved ticks; otherwise the defaults.
                const saved = selectedRef.current.find((s) => s.path === openedPrefix);
                const restore = saved && !!saved.includeSubfolders === recursive;
                if (draft && draft.rootUri === currentRoot) {
                    // Changing recursive mode keeps edits and ticks for sources
                    // that remain visible; newly discovered files use defaults.
                    setRenames(Object.fromEntries(openedFiles.filter((f) => draft.files[f.relativePath]?.value !== undefined)
                        .map((f) => [f.name, draft.files[f.relativePath].value])));
                    setPicked(new Set(openedFiles.filter((f) => draft.files[f.relativePath]?.picked ?? isDefaultPick(f)).map((f) => f.name)));
                } else {
                    setRenames(restore ? saved.renames || {} : {});
                    setPicked(new Set(restore ? saved.picked : openedFiles.filter(isDefaultPick).map((f) => f.name)));
                }
            })
            .catch((err) => {
                if (request !== browseRequest.current) return;
                setBrowseFailed(true);
                toast.error(errorMessage(err, 'Failed to browse S3'));
            })
            .finally(() => { if (request === browseRequest.current) setLoading(false); });
    }, [dispatch]);

    useEffect(() => {
        browse('');
        return () => { browseRequest.current += 1; };
    }, [browse]);

    const segments = prefix.split('/').filter(Boolean);
    const goTo = (index) => browse(index < 0 ? '' : `${segments.slice(0, index + 1).join('/')}/`);
    const existing = selected.find((s) => s.path === prefix);
    const addLimitReached = mode === 'ADD_EPISODES' && selected.length >= 1 && !existing;

    const pickable = files.filter(isPickable);
    const episodeFiles = files.filter((f) => f.kind === 'episode');
    const pickedEpisodeFiles = episodeFiles.filter((f) => picked.has(f.name));
    const allEpisodesPicked = episodeFiles.length > 0 && pickedEpisodeFiles.length === episodeFiles.length;
    const somePicked = pickedEpisodeFiles.length > 0 && !allEpisodesPicked;
    const summary = summarisePicks(files, picked);
    const orderedFiles = [...sourceFiles].sort((a, b) => {
        if ((a.kind === 'ignored') !== (b.kind === 'ignored')) return a.kind === 'ignored' ? 1 : -1;
        if (a.isMp4 !== b.isMp4) return a.isMp4 ? -1 : 1;
        if (!a.isMp4) return 0;
        const number = (file) => isTrailerLike(file) ? 0 : (file.episodeNumber ?? Number(file.reason?.match(/episode (\d+)/i)?.[1] ?? Infinity));
        return number(a) - number(b);
    }).map((file) => renames[file.name] === undefined ? file : renamedFile(file, renames[file.name]));
    const invalidNames = files.some((f) => picked.has(f.name) && f.invalidName);
    const unsavedChanges = existing && (includeSubfolders !== existing.includeSubfolders
        || JSON.stringify(renames) !== JSON.stringify(existing.renames)
        || picked.size !== existing.picked.length || existing.picked.some((name) => !picked.has(name)));
    const canSelect = !invalidNames && !loading && !browseFailed && prefix && pickable.length > 0 && summary.episodes > 0
        && !summary.clashes.length && !summary.trailerClash && !summary.missing.length && !addLimitReached;

    const editEpisodeNumber = (file, value) => {
        setRenames((prev) => ({ ...prev, [file.name]: value }));
        if (!isPickable(file)) setPicked((prev) => new Set([...prev, file.name]));
    };
    const sortedRenames = renumberInOrder(orderedFiles, renames, picked);
    const canSortRemaining = !loading && !creating && !browseFailed && sortedRenames
        && orderedFiles.some((file) => sortedRenames[file.name] !== undefined
            && sortedRenames[file.name] !== (renames[file.name] ?? episodeValue(file)));
    const sortRemaining = () => {
        if (canSortRemaining) setRenames(sortedRenames);
    };

    const toggleFile = (name) => setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    const toggleAllEpisodes = () => setPicked((prev) => {
        const next = new Set(prev);
        // Off clears every episode (clash files too); on ticks the importable ones.
        files.filter((f) => !isTrailerLike(f) && f.isMp4).forEach((f) => next.delete(f.name));
        if (!allEpisodesPicked) episodeFiles.forEach((f) => next.add(f.name));
        return next;
    });
    const selectAllCheckbox = useRef(null);
    useEffect(() => { if (selectAllCheckbox.current) selectAllCheckbox.current.indeterminate = somePicked; }, [somePicked]);

    const saveCurrent = () => {
        if (!canSelect) return;
        // Names and subfolders use explicit mappings; unchanged direct folders
        // retain the original selection format.
        const everything = pickable.every((f) => picked.has(f.name));
        const entry = {
            path: prefix,
            title: existing?.title ?? rootFolderTitle(prefix),
            files: includeSubfolders || Object.keys(renames).length
                ? files.filter((f) => picked.has(f.name)).map((f) => ({ source: f.name,
                    name: isTrailerLike(f) ? 'trailer.mp4' : `${f.episodeNumber}.mp4` }))
                : everything ? null : files.filter((f) => picked.has(f.name)).map((f) => f.name),
            renames: { ...renames },
            picked: [...picked],
            includeSubfolders,
            episodes: summary.episodes,
            totalEpisodes: files.filter((f) => f.isMp4 && !isTrailerLike(f)).length,
            trailer: summary.trailer,
        };
        setSelected((prev) => (existing ? prev.map((s) => (s.path === prefix ? entry : s)) : [...prev, entry]));
    };

    const invalidTitles = mode === 'NEW_SERIES' && selected.some((s) => !s.title.trim() || s.title.trim().length > 255);

    const submitBatch = (choices = []) => {
        setExistingMatches([]);
        setCreating(true);
        const selections = selected.filter((s) => s.files).map((s) => ({ path: s.path, files: s.files }));
        dispatch(action.createBulkImportBatch({
            append_paths: selected.map((s) => s.path),
            label: label.trim() || undefined,
            client_request_id: requestId.current,
            ...(selections.length && { selections }),
            ...(mode === 'NEW_SERIES' && { series_titles: selected.map((s) => ({ path: s.path, title: s.title.trim() })) }),
            ...(choices.length && { existing_series_choices: choices }),
            ...(mode === 'ADD_EPISODES' && { target_series_id: targetSeries.id }),
        }))
            .then((res) => {
                const created = res?.responseDetails || {};
                (created.parseErrors || []).forEach((e) => toast.warn(`${e.appendPath}: ${e.message}`));
                const id = created.batch?.id;
                // Check right away so the batch opens on its preview.
                return dispatch(action.validateBulkImportBatch(id))
                    .catch((err) => toast.error(errorMessage(err, 'The import was created, but checking it failed')))
                    .then(() => onCreated(id));
            })
            .catch((err) => toast.error(errorMessage(err, 'Failed to create the import')))
            .finally(() => setCreating(false));
    };

    const create = () => {
        if (invalidTitles || creating || unsavedChanges) return;
        if (mode === 'ADD_EPISODES') { submitBatch(); return; }
        setCreating(true);
        const selections = selected.filter((s) => s.files).map((s) => ({ path: s.path, files: s.files }));
        dispatch(action.checkBulkImportExistingSeries({ append_paths: selected.map((s) => s.path), selections }))
            .then((res) => {
                const matches = res?.responseDetails?.matches || [];
                if (matches.length) {
                    setExistingMatches(matches);
                    setExistingChoices({});
                    setCreating(false);
                } else {
                    submitBatch();
                }
            })
            .catch((err) => {
                toast.error(errorMessage(err, 'Could not check existing series'));
                setCreating(false);
            });
    };

    const confirmExistingChoices = () => {
        const choices = existingMatches.map((match) => {
            const selectedChoice = existingChoices[match.path];
            return selectedChoice === 'create_new'
                ? { path: match.path, action: 'create_new' }
                : { path: match.path, action: 'reuse', series_id: Number(selectedChoice.split(':')[1]) };
        });
        submitBatch(choices);
    };

    let selectHint = null;
    if (prefix && !loading) {
        if (addLimitReached) selectHint = 'One folder per import when adding episodes.';
        else if (invalidNames) selectHint = 'Correct the selected episode numbers before importing.';
        else if (!preview) selectHint = 'No MP4 files here — enable Import from subfolder or open the folder that holds the episodes.';
        else if (summary.clashes.length) selectHint = `More than one file ticked for episode ${summary.clashes.join(', ')} — keep one.`;
        else if (summary.trailerClash) selectHint = 'More than one trailer ticked — keep one.';
        else if (summary.missing.length) selectHint = `Missing episode numbers: ${summary.missing.join(', ')}. Include or renumber these episodes before saving.`;
        else if (unsavedChanges) selectHint = 'Click Update Selected Series to save your file changes before uploading.';
        else if (!summary.episodes) selectHint = 'Tick at least one episode.';
    }

    let trailerNote = 'No trailer selected — The first Episode will be used';
    if (summary.trailer) trailerNote = 'Trailer included';
    if (mode === 'ADD_EPISODES') trailerNote = 'using the Series Trailer';

    return (
        <>
            <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div className="bsi-card" style={{ flex: '1 1 560px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 12px 8px 6px', flexWrap: 'wrap' }}>
                        <nav aria-label="Folder path" title={rootUri ? `${rootUri}${prefix}` : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, flexWrap: 'wrap', minWidth: 0, flex: '1 1 320px', overflowWrap: 'anywhere' }}>
                            <button type="button" className="bsi-icon-btn" aria-label="Up one folder" disabled={!prefix} onClick={() => goTo(segments.length - 2)}>
                                <i className="fas fa-chevron-left" aria-hidden="true" />
                            </button>
                            <button type="button" className="bsi-link-btn" disabled={loading || !rootUri} title={rootUri || undefined}
                                style={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere', textAlign: 'left' }}
                                onClick={() => goTo(-1)}>{rootUri || (loading ? 'Loading S3 root…' : 'S3 root unavailable')}</button>
                            {segments.map((seg, i) => (
                                <React.Fragment key={`${seg}-${i}`}>
                                    {i > 0 && <span style={{ color: '#5a5a6a' }}>/</span>}
                                    {i === segments.length - 1
                                        ? <span style={{ color: '#fff', fontWeight: 600 }}>{seg}</span>
                                        : <button type="button" className="bsi-link-btn" onClick={() => goTo(i)}>{seg}</button>}
                                </React.Fragment>
                            ))}
                        </nav>
                        <button type="button" className="bsi-btn outline sm" disabled={!canSortRemaining} onClick={sortRemaining}>
                            Sort by Episode Number
                        </button>
                        <button type="button" className="bsi-btn outline sm" disabled={!canSelect} onClick={saveCurrent}>
                            <i className={`fas ${existing ? 'fa-check' : 'fa-plus'}`} aria-hidden="true" />
                            {existing ? 'Update Selected Series' : mode === 'ADD_EPISODES' ? 'Select these files' : 'Import Series'}
                        </button>
                    </div>
                    {prefix && (
                        <div style={{ padding: '10px 18px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
                                <input type="checkbox" className="bsi-cb" checked={includeSubfolders} disabled={loading || creating}
                                    onChange={(e) => browse(prefix, e.target.checked, { rootUri,
                                        files: Object.fromEntries(files.map((f) => [f.relativePath, { value: renames[f.name], picked: picked.has(f.name) }])),
                                    })} />
                                Import from subfolder
                            </label>
                            <p className="bsi-muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
                                Include MP4s from nested folders. Folders (01, 02, 03) determine episode numbers. Use "01" or "trailer" for episode number. Sort by Episode Number numbers selected episodes consecutively from top to bottom, starting with the first episode’s number. Trailers are skipped.
                            </p>
                        </div>
                    )}
                    {preview && (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '9px 18px', background: 'rgba(0,168,243,.06)', borderTop: '1px solid rgba(255,255,255,.06)', fontSize: 13, color: '#9a9aaa' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                                <input ref={selectAllCheckbox} type="checkbox" className="bsi-cb" checked={allEpisodesPicked}
                                    disabled={!episodeFiles.length} onChange={toggleAllEpisodes} />
                                Select All
                            </label>
                            <span><strong style={{ color: '#fff' }}>{summary.episodes}</strong> of {files.filter((f) => f.isMp4 && !isTrailerLike(f)).length} Episodes selected</span>
                            <span>{trailerNote}</span>
                            {preview.ignoredCount > 0 && <span>{preview.ignoredCount} non-MP4 ignored</span>}
                        </div>
                    )}
                    {selectHint && <div className="bsi-row" role={summary.missing.length || summary.clashes.length || summary.trailerClash || invalidNames ? 'alert' : undefined}
                        style={{ fontSize: 13, color: summary.missing.length || summary.clashes.length || summary.trailerClash || invalidNames ? '#ffc9c3' : '#8a8a9a' }}>{selectHint}</div>}
                    {loading && <div className="bsi-row bsi-muted">Loading…</div>}
                    {!loading && browseFailed && <div className="bsi-row" role="alert">Could not load the S3 location. <button type="button" className="bsi-link-btn" onClick={() => browse(prefix)}>Retry</button></div>}
                    {!loading && !browseFailed && folders.length === 0 && files.length === 0 && <div className="bsi-row bsi-muted">Empty folder.</div>}
                    {!loading && folders.map((f) => (
                        <button key={f.relativePath} type="button" className="bsi-row clickable"
                            style={{ gridTemplateColumns: '22px 22px minmax(0,1fr) auto', width: '100%', background: 'transparent', border: 'none', borderTop: '1px solid rgba(255,255,255,.06)', color: 'inherit', fontFamily: 'inherit', textAlign: 'left' }}
                            onClick={() => browse(f.relativePath)}>
                            <span />
                            <i className="fas fa-folder" style={{ color: '#38bdf8' }} aria-hidden="true" />
                            <span style={{ color: '#fff' }}>{f.name}</span>
                            {selected.some((s) => s.path === f.relativePath) && <span className="bsi-badge blue">Selected</span>}
                        </button>
                    ))}
                    {!loading && orderedFiles.map((file) => {
                        const [tagLabel, tone] = FILE_TAG[file.kind] || [null, 'gray'];
                        const canTick = isPickable(file) || (file.isMp4 && renames[file.name] !== undefined);
                        const ticked = canTick && picked.has(file.name);
                        return (
                            <div key={file.relativePath} className="bsi-row bsi-import-file" style={{ opacity: canTick && !ticked ? 0.6 : 1 }}>
                                {canTick
                                    ? <input type="checkbox" className="bsi-cb" checked={ticked} onChange={() => toggleFile(file.name)} aria-label={`Include ${file.name}`} />
                                    : <span />}
                                <i className={`fas ${file.isMp4 ? 'fa-file-video' : 'fa-file'}`} style={{ color: file.kind === 'episode' ? '#4ade80' : '#8a8a9a' }} aria-hidden="true" />
                                <span className="bsi-file-details">
                                    <span className="bsi-file-name" title={file.name}
                                        style={{ color: file.kind === 'ignored' ? '#9a9aaa' : '#fff' }}>{file.name}</span>
                                    {file.isMp4 && (
                                        <input className="bsi-input bsi-episode-number" type="text"
                                            aria-label={`Episode number for ${file.name}`} maxLength={10} disabled={creating}
                                            aria-invalid={!!file.invalidName} placeholder="01 / trailer"
                                            value={renames[file.name] ?? episodeValue(file)}
                                            onChange={(e) => editEpisodeNumber(file, e.target.value)} />
                                    )}
                                    {file.reason && file.kind === 'skipped' && (
                                        <span className="bsi-file-error">{file.reason}{isClash(file) ? ' — tick only one to import it' : ''}</span>
                                    )}
                                </span>
                                <span className={`bsi-badge ${tone}`}>{tagLabel || `Episode ${file.episodeNumber}`}</span>
                                <span className="bsi-muted" style={{ textAlign: 'right', fontSize: 13 }}>{formatSize(file.size)}</span>
                            </div>
                        );
                    })}
                </div>

                <aside className="bsi-card" aria-label="Selected folders" style={{ flex: '0 1 360px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#fff' }}>
                            {mode === 'ADD_EPISODES' ? `Episodes for ${targetSeries?.title}` : 'Selected Series'}
                        </h2>
                        <span className="bsi-badge blue">{selected.length}</span>
                    </div>
                    {selected.length === 0 && (
                        <div className="bsi-muted" style={{ padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,.06)', fontSize: 16 }}>
                            Open the folder containing the series episode MP4 files, select the required episodes, and click "Import Series"
                        </div>
                    )}
                    {selected.map((s, index) => (
                        <div key={s.path} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px 8px 18px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 13, color: '#7dd3fc' }}>Import Path</span>
                                <button type="button" className="bsi-link-btn bsi-title" style={{ fontSize: 13, color: '#fff', overflowWrap: 'anywhere' }} onClick={() => browse(s.path)}>
                                    {s.path}
                                </button>
                                {mode === 'NEW_SERIES' && (
                                    <>
                                        <label htmlFor={`bsi-series-title-${index}`} style={{ fontSize: 13, marginTop: 6, color: '#7dd3fc' }}>Series Title</label>
                                        <input id={`bsi-series-title-${index}`} className="bsi-input" required maxLength={255}
                                            aria-label={`Series title for ${s.path}`} value={s.title} disabled={creating}
                                            aria-invalid={!s.title.trim() || s.title.trim().length > 255}
                                            onChange={(e) => {
                                                const title = e.target.value;
                                                setSelected((prev) => prev.map((entry) => entry.path === s.path ? { ...entry, title } : entry));
                                            }} />
                                        {(!s.title.trim() || s.title.trim().length > 255) && (
                                            <span style={{ color: '#ff7a6e', fontSize: 12 }}>Enter a series title of 1–255 characters.</span>
                                        )}
                                    </>
                                )}
                                <span style={{ fontSize: 12, color: '#7dfc9d'}}>
                                    {s.files ? `${s.episodes} of ${s.totalEpisodes} Episodes` : `All ${s.totalEpisodes} Episodes`}
                                    {mode !== 'ADD_EPISODES' && (s.trailer ? ' · Trailer' : ' · No trailer (Episode 1 used)')}
                                </span>
                            </span>
                            <button type="button" className="bsi-icon-btn" aria-label={`Remove ${s.path}`} onClick={() => setSelected((prev) => prev.filter((x) => x.path !== s.path))}>
                                <i className="fas fa-times" aria-hidden="true" />
                            </button>
                        </div>
                    ))}
                    <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,.06)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label htmlFor="bsi-label" style={{ fontSize: 13, fontWeight: 600 }}>Import Label (optional)</label>
                        <input id="bsi-label" className="bsi-input" maxLength={255} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Useful for identifying this specific import." />
                        <p className="bsi-muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <i className="fas fa-info-circle" aria-hidden="true" style={{ color: '#38bdf8', marginTop: 4, flexShrink: 0 }} />
                            <span>IMPORTANT: Verify the Import Path, Trailer, and Episode Numbering before proceeding.</span>
                        </p>
                        <p className="bsi-muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ color: '#fbbf24', marginTop: 4, flexShrink: 0 }} />
                            <span>WARNING: Any incorrect episode ordering or video files must be manually corrected after the import.</span>
                        </p>
                    </div>
                </aside>
            </div>

            {mode === 'NEW_SERIES' && selected.length > 0 && (
                <div className="bsi-callout blue" role="note">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>New series are created in DRAFT status. Nothing is visible to viewers until someone publishes it.</span>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <button type="button" className="bsi-btn ghost" onClick={onBack}>Back</button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 14, marginLeft: 'auto' }}>
                    <span className="bsi-muted" style={{ fontSize: 14 }}>{selected.length} folder{selected.length === 1 ? '' : 's'} selected</span>
                    <button type="button" className="bsi-btn primary" style={{ whiteSpace: 'nowrap' }} disabled={creating || selected.length === 0 || invalidTitles || unsavedChanges} onClick={create}>
                        {creating ? <span className="spinner-border spinner-border-sm" /> : 'Upload Selected Series'}
                    </button>
                </div>
            </div>

            {existingMatches.length > 0 && (
                <>
                    <div className="bsi-overlay" onClick={() => setExistingMatches([])} />
                    <div className="bsi-modal" role="dialog" aria-modal="true" aria-labelledby="bsi-existing-title">
                        <h2 id="bsi-existing-title" style={{ margin: 0, color: '#fff', fontSize: 21 }}>Series already imported from this path</h2>
                        <p className="bsi-muted" style={{ margin: 0 }}>Choose whether to use the imported series or create a separate new series from the same files.</p>
                        {existingMatches.map((match) => (
                            <div key={match.path} style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <strong style={{ color: '#fff', overflowWrap: 'anywhere' }}>{match.path}</strong>
                                <select className="bsi-input" aria-label={`Choice for ${match.path}`} value={existingChoices[match.path] || ''}
                                    onChange={(e) => setExistingChoices((prev) => ({ ...prev, [match.path]: e.target.value }))}>
                                    <option value="">Choose an action</option>
                                    {match.series.filter((series) => Number(series.is_deleted) !== 1).map((series) => (
                                        <option key={series.id} value={`reuse:${series.id}`}>Use series #{series.id} — {series.title}</option>
                                    ))}
                                    <option value="create_new">Create a new series from this path</option>
                                </select>
                            </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button type="button" className="bsi-btn ghost" onClick={() => setExistingMatches([])}>Cancel</button>
                            <button type="button" className="bsi-btn primary" disabled={creating || existingMatches.some((match) => !existingChoices[match.path])}
                                onClick={confirmExistingChoices}>Continue</button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

function formatSize(bytes) {
    const b = Number(bytes) || 0;
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
    if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
    if (b >= 1024) return `${Math.round(b / 1024)} KB`;
    return `${b} B`;
}

const NewImport = ({ onCancel, onCreated }) => {
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState('NEW_SERIES');
    const [targetSeries, setTargetSeries] = useState(null);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="bsi-crumb">
                    <button type="button" className="bsi-link-btn" onClick={onCancel}>Bulk Series Import</button> / New import
                </div>
                <h1 className="bsi-h1">{step === 1 ? 'What are you importing?' : 'Pick series folders'}</h1>
                {step === 2 && (
                    <div className="bsi-muted" style={{ fontSize: 14 }}>
                        Open a series folder, choose whether to include subfolders, and review the episode numbers and trailer before importing.
                    </div>
                )}
            </div>
            <Steps current={step} />
            {step === 1 && (
                <TypeStep mode={mode} setMode={setMode} targetSeries={targetSeries} setTargetSeries={setTargetSeries}
                    onCancel={onCancel} onNext={() => setStep(2)} />
            )}
            {step === 2 && <FolderStep mode={mode} targetSeries={targetSeries} onBack={() => setStep(1)} onCreated={onCreated} />}
        </>
    );
};

export default NewImport;
